import { useState, useRef } from "react";
import { Camera, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let w = img.width;
        let h = img.height;
        if (w > maxWidth) {
          h = (h * maxWidth) / w;
          w = maxWidth;
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => resolve(new File([blob], file.name, { type: "image/jpeg" })),
          "image/jpeg",
          quality
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

export const LabelScanner = ({ onScanResult }) => {
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const fileInputRef = useRef(null);

  const handleCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreview(ev.target.result);
      setDialogOpen(true);
      processImage(file);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const processImage = async (file) => {
    setScanning(true);
    try {
      const compressed = await compressImage(file, 1024, 0.6);
      const formData = new FormData();
      formData.append("file", compressed);

      const res = await axios.post(`${API}/scan-label`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 25000,
      });

      const { batch_number, pack_date } = res.data;

      if (batch_number || pack_date) {
        onScanResult({ batchNumber: batch_number, packDate: pack_date });
        toast.success(
          `Extracted: Batch ${batch_number || "N/A"}, Pack Date ${pack_date || "N/A"}`
        );
        setDialogOpen(false);
      } else {
        toast.error("Could not extract data from the label");
      }
    } catch (err) {
      console.error("Scan error:", err);
      toast.error(err.response?.data?.detail || "Failed to scan label");
    } finally {
      setScanning(false);
    }
  };

  const openCamera = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCapture}
        className="hidden"
        data-testid="scan-file-input"
      />

      <Button
        data-testid="scan-label-btn"
        variant="secondary"
        onClick={openCamera}
        className="h-14 px-5 rounded-sm border-2 border-dashed border-primary/40 hover:border-primary hover:bg-primary/5 text-primary font-bold uppercase tracking-wider text-xs transition-all w-full sm:w-auto"
      >
        <Camera className="w-5 h-5 mr-2" />
        SCAN LABEL
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle
              className="uppercase tracking-wider"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {scanning ? "SCANNING LABEL..." : "SCAN RESULT"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {preview && (
              <div className="relative rounded-sm overflow-hidden border border-border">
                <img
                  src={preview}
                  alt="Scanned label"
                  className="w-full h-auto max-h-[300px] object-contain bg-black/20"
                  data-testid="scan-preview-image"
                />
                {scanning && (
                  <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-3">
                    <Loader2 className="w-10 h-10 text-primary animate-spin" />
                    <span className="text-sm font-bold tracking-wider uppercase text-primary">
                      READING LABEL DATA...
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          {!scanning && (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => { setDialogOpen(false); setPreview(null); }}
                className="flex-1 rounded-sm uppercase tracking-wider text-xs"
              >
                CLOSE
              </Button>
              <Button
                onClick={openCamera}
                className="flex-1 bg-primary text-primary-foreground rounded-sm uppercase tracking-wider text-xs"
              >
                <Camera className="w-4 h-4 mr-2" />
                RETRY
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
